import { useEffect, useCallback, useRef } from 'react';
import { useToast } from '@/components/common';
import type { AxiosError } from 'axios';

/**
 * Error category for API errors
 */
export type ErrorCategory = 'network' | 'server' | 'not_found' | 'auth' | 'other';

/**
 * Parsed API error structure
 */
export interface ParsedApiError {
  category: ErrorCategory;
  statusCode: number | null;
  message: string;
  detail?: string;
}

/**
 * Event types for the error handler
 */
export type GlobalErrorEvent = {
  type: 'api_error';
  error: ParsedApiError;
  originalError: Error;
};

/**
 * Event emitter for global error handling
 * Used to communicate between QueryClient (outside React) and React components
 */
class ErrorEventEmitter {
  private listeners: Set<(event: GlobalErrorEvent) => void> = new Set();

  subscribe(listener: (event: GlobalErrorEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(event: GlobalErrorEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }
}

// Singleton instance for global error events
export const globalErrorEmitter = new ErrorEventEmitter();

/**
 * Parse an error into a structured format
 */
export function parseApiError(error: unknown): ParsedApiError {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
    const statusCode = axiosError.response?.status ?? null;

    // Determine category based on status code or error type
    let category: ErrorCategory = 'other';
    let message = 'An unexpected error occurred';

    if (!axiosError.response) {
      // No response - network error
      category = 'network';
      message = 'Network error. Please check your connection.';
    } else if (statusCode) {
      if (statusCode === 401 || statusCode === 403) {
        category = 'auth';
        message = statusCode === 401 ? 'Authentication required' : 'Access denied';
      } else if (statusCode === 404) {
        category = 'not_found';
        message = 'The requested resource was not found';
      } else if (statusCode >= 500) {
        category = 'server';
        message = 'Server error. Please try again later.';
      } else if (statusCode >= 400) {
        category = 'other';
        message = 'Request failed';
      }
    }

    // Extract detail from response
    const detail =
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      axiosError.message;

    return {
      category,
      statusCode,
      message,
      detail: detail !== message ? detail : undefined,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Check for network-related error messages
    if (
      error.message.includes('Network Error') ||
      error.message.includes('fetch') ||
      error.message.includes('ECONNREFUSED')
    ) {
      return {
        category: 'network',
        statusCode: null,
        message: 'Network error. Please check your connection.',
        detail: error.message,
      };
    }

    return {
      category: 'other',
      statusCode: null,
      message: error.message || 'An unexpected error occurred',
    };
  }

  // Unknown error type
  return {
    category: 'other',
    statusCode: null,
    message: 'An unexpected error occurred',
  };
}

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Check if an error should be suppressed from global handling
 * (e.g., auth errors are handled separately)
 */
export function shouldSuppressError(error: ParsedApiError): boolean {
  // Suppress 401/403 errors - auth is handled separately
  if (error.category === 'auth') {
    return true;
  }
  return false;
}

/**
 * Get user-friendly error title based on category
 */
function getErrorTitle(category: ErrorCategory): string {
  switch (category) {
    case 'network':
      return 'Connection Error';
    case 'server':
      return 'Server Error';
    case 'not_found':
      return 'Not Found';
    case 'auth':
      return 'Authentication Error';
    case 'other':
    default:
      return 'Error';
  }
}

/**
 * Options for the global error handler hook
 */
export interface UseGlobalErrorHandlerOptions {
  /** Whether to show toasts for errors (default: true) */
  showToasts?: boolean;
  /** Custom error handler callback */
  onError?: (error: ParsedApiError, originalError: Error) => void;
}

/**
 * Return type for the global error handler hook
 */
export interface UseGlobalErrorHandlerReturn {
  /** Manually handle an error */
  handleError: (error: unknown) => void;
}

/**
 * Hook for handling global API errors with toast notifications.
 *
 * This hook subscribes to the global error emitter and displays
 * toast notifications for API errors. Auth errors (401/403) are
 * suppressed as they are handled separately.
 *
 * @example
 * ```tsx
 * // In a component that should show error toasts
 * function App() {
 *   useGlobalErrorHandler();
 *   return <RouterProvider router={router} />;
 * }
 *
 * // With custom error handling
 * useGlobalErrorHandler({
 *   onError: (error) => {
 *     analytics.trackError(error);
 *   },
 * });
 * ```
 */
export function useGlobalErrorHandler(
  options: UseGlobalErrorHandlerOptions = {}
): UseGlobalErrorHandlerReturn {
  const { showToasts = true, onError } = options;
  const toast = useToast();

  // Use ref to avoid stale closures in the event handler
  const optionsRef = useRef({ showToasts, onError });
  useEffect(() => {
    optionsRef.current = { showToasts, onError };
  }, [showToasts, onError]);

  const handleError = useCallback(
    (error: unknown) => {
      const parsedError = parseApiError(error);

      // Skip suppressed errors
      if (shouldSuppressError(parsedError)) {
        return;
      }

      // Call custom error handler if provided
      if (optionsRef.current.onError) {
        optionsRef.current.onError(
          parsedError,
          error instanceof Error ? error : new Error(String(error))
        );
      }

      // Show toast notification
      if (optionsRef.current.showToasts) {
        const title = getErrorTitle(parsedError.category);
        const description = parsedError.detail || parsedError.message;
        toast.error(title, description);
      }
    },
    [toast]
  );

  useEffect(() => {
    // Subscribe to global error events
    const unsubscribe = globalErrorEmitter.subscribe((event) => {
      if (event.type === 'api_error') {
        const { error: parsedError, originalError } = event;

        // Skip suppressed errors
        if (shouldSuppressError(parsedError)) {
          return;
        }

        // Call custom error handler if provided
        if (optionsRef.current.onError) {
          optionsRef.current.onError(parsedError, originalError);
        }

        // Show toast notification
        if (optionsRef.current.showToasts) {
          const title = getErrorTitle(parsedError.category);
          const description = parsedError.detail || parsedError.message;
          toast.error(title, description);
        }
      }
    });

    return unsubscribe;
  }, [toast]);

  return { handleError };
}

/**
 * Emit a global error event (for use in QueryClient callbacks)
 */
export function emitGlobalError(error: unknown): void {
  const parsedError = parseApiError(error);
  globalErrorEmitter.emit({
    type: 'api_error',
    error: parsedError,
    originalError: error instanceof Error ? error : new Error(String(error)),
  });
}
